import type { LeaveType } from '../network/leavePolicy';

export type TimeawayTypeCode =
  | 'holiday_paid'
  | 'holiday_unpaid'
  | 'working_remotely'
  | 'sick_leave'
  | 'parental_leave'
  | 'other_leave';

export type TimeawayDecisionCode =
  | 'pending'
  | 'approved'
  | 'declined'
  | 'awaiting_employee_updates';

export const TIMEAWAY_TYPE_OPTIONS: Array<{ value: TimeawayTypeCode; label: string }> = [
  { value: 'holiday_paid', label: 'Holiday (Paid)' },
  { value: 'holiday_unpaid', label: 'Holiday (Unpaid)' },
  { value: 'working_remotely', label: 'Working remotely' },
  { value: 'sick_leave', label: 'Sick leave' },
  { value: 'parental_leave', label: 'Parental leave' },
  { value: 'other_leave', label: 'Other leave' },
];

const DEFAULT_COLOURS: Record<TimeawayTypeCode, string> = {
  holiday_paid: '#ef4444',
  holiday_unpaid: '#eab308',
  working_remotely: '#10b981',
  sick_leave: '#0ea5e9',
  parental_leave: '#8b5cf6',
  other_leave: '#ec4899',
};

const DEFAULT_BUCKETS: Record<TimeawayTypeCode, LeaveType['allowance_key']> = {
  holiday_paid: 'paid_holiday',
  holiday_unpaid: 'unpaid_holiday',
  working_remotely: 'working_remotely',
  sick_leave: 'sick_leave',
  parental_leave: 'parental_leave',
  other_leave: 'other_leave',
};

/**
 * The built-in leave types, as the API seeds them for every company. Used
 * while a company's own types load, or if loading them fails.
 */
export const DEFAULT_LEAVE_TYPES: LeaveType[] = TIMEAWAY_TYPE_OPTIONS.map(
  (option, index) => ({
    key: option.value,
    name: option.label,
    colour: DEFAULT_COLOURS[option.value],
    paid: option.value !== 'holiday_unpaid',
    counts_against_allowance: true,
    allowance_key: DEFAULT_BUCKETS[option.value],
    requires_approval: true,
    allows_part_days: true,
    requires_note: false,
    active: true,
    sort_order: index,
    is_default: true,
  }),
);

const TYPE_LABELS: Record<string, string> = TIMEAWAY_TYPE_OPTIONS.reduce(
  (acc, item) => {
    acc[item.value] = item.label;
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Remembers the company's leave type names so tables can label requests of
 * custom types (getTimeawayTypeLabel is synchronous). useLeaveTypes() calls
 * this whenever types load.
 */
export function registerLeaveTypeLabels(types: Pick<LeaveType, 'key' | 'name'>[]) {
  for (const type of types) {
    if (type?.key && type?.name) TYPE_LABELS[type.key] = type.name;
  }
}

export function getTimeawayTypeLabel(type?: string) {
  if (!type) return '';
  return TYPE_LABELS[type] || type;
}

export function getTimeawayDecisionLabel(decision?: string) {
  switch (decision) {
    case 'pending':
      return 'Pending';
    case 'approved':
      return 'Approved';
    case 'declined':
      return 'Rejected';
    case 'awaiting_employee_updates':
      return 'Awaiting employee updates';
    default:
      return decision || '';
  }
}

